import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Loader2, Video, Image as ImageIcon, Upload, Play, Pause, Download,
  Sparkles, Type, Captions, Wand2, Music, Save, History as HistoryIcon,
  Trash2, ChevronLeft, LogOut, X, RefreshCw,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { authApi } from "../admin/api";
import { studiovideoApi } from "../studiovideo/api";
import { renderFrame, loadImageEl } from "../studiovideo/compositor";
import { exportMp4 } from "../studiovideo/export";
import {
  FORMATS, CTA_PRESETS, CTA_COLOR_OPTIONS, SIGNAL_COLOR_OPTIONS,
  ANIM_IN, TEXT_POSITIONS, IMAGE_DURATIONS, KEN_BURNS_OPTIONS,
  FADE_OPTIONS, GLITCH_OPTIONS, OVERLAY_OPTIONS, CROP_OPTIONS,
  defaultLayers,
} from "../studiovideo/presets";
import { usePageMeta } from "../hooks/usePageMeta";
import { readPrefill } from "../planner/helpers";

const LOGO_SRC = "/brand-assets/daos-logo.png";
const VIDEO_MAX_BYTES = 200 * 1024 * 1024;
const IMAGE_MAX_BYTES = 20 * 1024 * 1024;
const AUDIO_MAX_BYTES = 20 * 1024 * 1024;
const PREVIEW_MAX_LONG = 480; // px on the long side of the preview

export default function StudioVideo() {
  usePageMeta({ title: "Studio Video · Daos Ads" });
  useEffect(() => {
    let tag = document.querySelector('meta[name="robots"]');
    const prev = tag?.getAttribute("content") || null;
    if (!tag) { tag = document.createElement("meta"); tag.setAttribute("name", "robots"); document.head.appendChild(tag); }
    tag.setAttribute("content", "noindex,nofollow");
    return () => { if (prev === null) tag?.remove(); else tag?.setAttribute("content", prev); };
  }, []);
  const navigate = useNavigate();

  // Step 1 — source
  const [sourceKind, setSourceKind] = useState(null); // 'video' | 'image' | null
  const [sourceFile, setSourceFile] = useState(null);
  const [sourceUrl, setSourceUrl] = useState(null);
  const [videoEl, setVideoEl] = useState(null);
  const [imageEl, setImageEl] = useState(null);
  const [videoMeta, setVideoMeta] = useState(null); // { duration, width, height }
  const [imageDuration, setImageDuration] = useState(10);

  // Step 2 — format
  const [formatId, setFormatId] = useState("reels");
  const format = useMemo(() => FORMATS.find((f) => f.id === formatId) || FORMATS[0], [formatId]);

  // Step 3 — layer config
  const [layers, setLayers] = useState(defaultLayers());
  const [projectName, setProjectName] = useState("");

  // Audio track (optional)
  const [audioFile, setAudioFile] = useState(null);

  // Playback / preview
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const startedAtRef = useRef(0);
  const currentTRef = useRef(0);
  const [playing, setPlaying] = useState(false);
  const [logoImg, setLogoImg] = useState(null);
  const [nowT, setNowT] = useState(0);

  // Whisper transcribe UI
  const [transcribing, setTranscribing] = useState(false);

  // Export
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportLog, setExportLog] = useState("");
  const [exportResult, setExportResult] = useState(null);

  // History
  const [history, setHistory] = useState([]);
  const [savingProject, setSavingProject] = useState(false);

  const totalSeconds = useMemo(() => {
    if (sourceKind === "video") return videoMeta?.duration || 0;
    if (sourceKind === "image") return imageDuration;
    return 0;
  }, [sourceKind, videoMeta, imageDuration]);

  // Load brand logo once
  useEffect(() => { loadImageEl(LOGO_SRC).then(setLogoImg).catch(() => setLogoImg(null)); }, []);

  // Load history on mount
  useEffect(() => {
    (async () => {
      try {
        const d = await studiovideoApi.listProjects({ limit: 24 });
        setHistory(d.items || []);
      } catch (e) { /* silent */ }
    })();
  }, []);

  // Cleanup blob URLs on unmount
  useEffect(() => () => { if (sourceUrl) URL.revokeObjectURL(sourceUrl); }, [sourceUrl]);

  // Preview dimensions
  const previewStyle = useMemo(() => {
    const s = PREVIEW_MAX_LONG / Math.max(format.width, format.height);
    return { width: format.width * s, height: format.height * s, maxWidth: "100%" };
  }, [format]);

  const source = useMemo(() => {
    if (sourceKind === "video" && videoEl) return { type: "video", el: videoEl };
    if (sourceKind === "image" && imageEl) return { type: "image", el: imageEl };
    return null;
  }, [sourceKind, videoEl, imageEl]);

  // Redraw one frame (static) when config changes but not playing
  useEffect(() => {
    if (playing) return;
    const canvas = canvasRef.current;
    if (!canvas || !source) return;
    canvas.width = format.width;
    canvas.height = format.height;
    const ctx = canvas.getContext("2d");
    // Keep the video element in sync with the scrubber when paused
    if (source.type === "video" && Math.abs(source.el.currentTime - nowT) > 0.1) {
      try { source.el.currentTime = nowT; } catch (_) { /* seeking */ }
    }
    renderFrame(ctx, source, layers, {
      W: format.width, H: format.height, total: totalSeconds || 1,
      t: nowT, logo: logoImg,
    });
  }, [source, layers, format, totalSeconds, logoImg, playing, nowT]);

  // Playback loop
  useEffect(() => {
    if (!playing || !source) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = format.width;
    canvas.height = format.height;
    const ctx = canvas.getContext("2d");

    startedAtRef.current = performance.now() - currentTRef.current * 1000;
    if (source.type === "video") { source.el.play().catch(() => {}); }

    const loop = () => {
      const t = (performance.now() - startedAtRef.current) / 1000;
      if (t >= totalSeconds) {
        currentTRef.current = totalSeconds;
        setNowT(totalSeconds);
        setPlaying(false);
        if (source.type === "video") source.el.pause();
        return;
      }
      currentTRef.current = t;
      setNowT(t);
      renderFrame(ctx, source, layers, {
        W: format.width, H: format.height, total: totalSeconds || 1, t, logo: logoImg,
      });
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafRef.current);
      if (source.type === "video") source.el.pause();
    };
  }, [playing, source, layers, format, totalSeconds, logoImg]);

  // ================= FILE HANDLERS =================
  const onPickVideo = async (file) => {
    if (!file) return;
    if (!/^video\//.test(file.type)) { toast.error("Solo archivos de video"); return; }
    if (file.size > VIDEO_MAX_BYTES) { toast.error("Video > 200MB. Bajá la resolución o duración."); return; }
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.src = url; v.muted = true; v.playsInline = true; v.crossOrigin = "anonymous";
    await new Promise((res, rej) => {
      v.onloadedmetadata = () => res();
      v.onerror = () => rej(new Error("meta"));
    }).catch(() => { toast.error("No se pudo leer el video"); URL.revokeObjectURL(url); return; });
    setSourceKind("video");
    setSourceFile(file); setSourceUrl(url); setVideoEl(v);
    setVideoMeta({ duration: v.duration || 10, width: v.videoWidth, height: v.videoHeight });
    setProjectName(file.name.replace(/\.[^.]+$/, ""));
    currentTRef.current = 0;
    setPlaying(false);
    toast.success(`Video listo · ${v.videoWidth}×${v.videoHeight} · ${v.duration.toFixed(1)}s`);
  };

  const onPickImage = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error("Solo archivos de imagen"); return; }
    if (file.size > IMAGE_MAX_BYTES) { toast.error("Imagen > 20MB"); return; }
    const url = URL.createObjectURL(file);
    try {
      const img = await loadImageEl(url);
      setSourceKind("image");
      setSourceFile(file); setSourceUrl(url); setImageEl(img);
      setProjectName(file.name.replace(/\.[^.]+$/, ""));
      currentTRef.current = 0;
      setPlaying(false);
      toast.success(`Imagen lista · ${img.naturalWidth}×${img.naturalHeight}`);
    } catch (e) { toast.error("No se pudo leer la imagen"); URL.revokeObjectURL(url); }
  };

  const onPickAudio = async (file) => {
    if (!file) return;
    if (!/^audio\//.test(file.type)) { toast.error("Solo archivos de audio (MP3/WAV)"); return; }
    if (file.size > AUDIO_MAX_BYTES) { toast.error("Audio > 20MB"); return; }
    setAudioFile(file);
    setLayers((L) => ({ ...L, audio: { ...L.audio, enabled: true } }));
    toast.success(`Audio cargado · ${file.name}`);
  };

  // Prefill coming from /planner ("Generar video en Studio Video")
  const prefillDone = useRef(false);
  useEffect(() => {
    if (prefillDone.current) return;
    prefillDone.current = true;
    const pre = readPrefill();
    if (!pre || pre.target !== "video") return;
    if (pre.formatId) setFormatId(pre.formatId);
    if (pre.duration) setImageDuration(pre.duration);
    if (pre.projectName) setProjectName(pre.projectName);
    setLayers((L) => ({
      ...L,
      brand: { ...L.brand, cta: pre.cta || L.brand.cta, ctaColor: pre.ctaColor || L.brand.ctaColor },
      text: {
        ...L.text,
        enabled: true,
        headline: pre.headline || L.text.headline,
        subtitle: pre.subtitle || "",
        gradientWords: pre.gradientWords || "",
        signalWords: pre.signalWords || "",
        signalColor: pre.signalColor || "amber",
        duration: Math.min(pre.duration || 15, 6),
      },
      effects: { ...L.effects, kenBurns: pre.kenBurns || L.effects.kenBurns, glowPulse: pre.glowPulse !== false },
    }));
    if (pre.backgroundDataUrl) {
      (async () => {
        try {
          const blob = await (await fetch(pre.backgroundDataUrl)).blob();
          const file = new File([blob], "planner-fondo.jpg", { type: blob.type || "image/jpeg" });
          await onPickImage(file);
          if (pre.projectName) setProjectName(pre.projectName);
        } catch (e) { /* ignore */ }
      })();
    }
    toast.success("Pieza del planner cargada");
  }, [onPickImage]);

  const resetSource = () => {    if (sourceUrl) URL.revokeObjectURL(sourceUrl);
    setSourceKind(null); setSourceFile(null); setSourceUrl(null);
    setVideoEl(null); setImageEl(null); setVideoMeta(null);
    setAudioFile(null);
    setLayers(defaultLayers());
    setExportResult(null);
    currentTRef.current = 0; setNowT(0); setPlaying(false);
  };

  // ================= WHISPER =================
  const onGenerateSubtitles = async () => {
    if (!sourceFile || sourceKind !== "video") { toast.error("Necesitas subir un video primero"); return; }
    setTranscribing(true);
    try {
      toast.message("Extrayendo audio del video…");
      const mod = await import("../studiovideo/export");
      const wav = await mod.extractAudioForWhisper(sourceFile);
      toast.message("Transcribiendo con Whisper…");
      const data = await studiovideoApi.transcribe(wav, { filename: "audio.wav" });
      const items = (data.segments || []).map((s) => ({
        start: Number(s.start) || 0, end: Number(s.end) || 0, text: String(s.text || "").trim(),
      })).filter((s) => s.text);
      setLayers((L) => ({ ...L, subtitles: { ...L.subtitles, enabled: true, mode: "ai", items } }));
      toast.success(`Subtítulos generados · ${items.length} segmentos`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo transcribir");
    } finally { setTranscribing(false); }
  };

  const addManualSubtitle = () => {
    setLayers((L) => ({
      ...L,
      subtitles: {
        ...L.subtitles, enabled: true, mode: L.subtitles.mode === "ai" ? "ai" : "manual",
        items: [...(L.subtitles.items || []), { start: nowT, end: Math.min(totalSeconds, nowT + 3), text: "Nuevo subtítulo" }],
      },
    }));
  };
  const updateSubtitle = (i, patch) => {
    setLayers((L) => {
      const items = [...L.subtitles.items];
      items[i] = { ...items[i], ...patch };
      return { ...L, subtitles: { ...L.subtitles, items } };
    });
  };
  const removeSubtitle = (i) => {
    setLayers((L) => ({ ...L, subtitles: { ...L.subtitles, items: L.subtitles.items.filter((_, idx) => idx !== i) } }));
  };

  // ================= EXPORT =================
  const onExport = async () => {
    if (!source || totalSeconds <= 0) { toast.error("Configura el origen primero"); return; }
    setExporting(true); setExportProgress(0); setExportLog(""); setExportResult(null);
    try {
      const result = await exportMp4({
        source, layers, format, totalSeconds,
        uploadedAudio: audioFile,
        onProgress: (p) => setExportProgress(p),
        onLog: (m) => setExportLog(m),
      });
      setExportResult(result);
      toast.success("MP4 listo para descargar");
      // Auto-save project (config + thumbnail)
      const canvas = canvasRef.current;
      if (canvas) {
        const off = document.createElement("canvas");
        const scale = 320 / Math.max(canvas.width, canvas.height);
        off.width = Math.round(canvas.width * scale);
        off.height = Math.round(canvas.height * scale);
        off.getContext("2d").drawImage(canvas, 0, 0, off.width, off.height);
        const thumb = off.toDataURL("image/jpeg", 0.8).split(",")[1];
        const payload = {
          name: projectName || `Proyecto ${new Date().toISOString().slice(0, 10)}`,
          source_type: sourceKind,
          source_filename: sourceFile?.name || null,
          source_duration: totalSeconds,
          format_id: formatId,
          duration_seconds: totalSeconds,
          layers,
          thumbnail_base64: thumb,
          thumbnail_mime: "image/jpeg",
        };
        try {
          const saved = await studiovideoApi.saveProject(payload);
          setHistory((h) => [saved, ...h].slice(0, 60));
        } catch (e) { /* silent — export already succeeded */ }
      }
    } catch (err) {
      toast.error(`Export falló: ${err?.message || err}`);
    } finally { setExporting(false); }
  };

  const downloadExport = () => {
    if (!exportResult) return;
    const a = document.createElement("a");
    a.href = exportResult.url;
    a.download = exportResult.filename;
    document.body.appendChild(a); a.click(); a.remove();
  };

  // ================= HISTORY =================
  const onSaveProject = async () => {
    const canvas = canvasRef.current;
    if (!canvas || !source) { toast.error("Configura el origen primero"); return; }
    setSavingProject(true);
    try {
      const off = document.createElement("canvas");
      const scale = 320 / Math.max(canvas.width, canvas.height);
      off.width = Math.round(canvas.width * scale);
      off.height = Math.round(canvas.height * scale);
      off.getContext("2d").drawImage(canvas, 0, 0, off.width, off.height);
      const thumb = off.toDataURL("image/jpeg", 0.8).split(",")[1];
      const payload = {
        name: projectName || `Proyecto ${new Date().toISOString().slice(0, 10)}`,
        source_type: sourceKind,
        source_filename: sourceFile?.name || null,
        source_duration: totalSeconds,
        format_id: formatId,
        duration_seconds: totalSeconds,
        layers,
        thumbnail_base64: thumb,
        thumbnail_mime: "image/jpeg",
      };
      const saved = await studiovideoApi.saveProject(payload);
      setHistory((h) => [saved, ...h].slice(0, 60));
      toast.success("Config guardada");
    } catch (err) {
      toast.error("No se pudo guardar la config");
    } finally { setSavingProject(false); }
  };

  const onLoadProject = async (id) => {
    try {
      const full = await studiovideoApi.getProject(id);
      setLayers(full.layers || defaultLayers());
      setFormatId(full.format_id || "reels");
      setProjectName(full.name || "");
      if (full.source_type === "image") setImageDuration(full.duration_seconds || 10);
      toast.success(`Config cargada · ahora subí el archivo "${full.source_filename || 'original'}" desde tu compu`);
    } catch (e) { toast.error("No se pudo cargar la config"); }
  };

  const onDeleteProject = async (id) => {
    const prev = history;
    setHistory((h) => h.filter((x) => x.id !== id));
    try { await studiovideoApi.deleteProject(id); toast.success("Proyecto eliminado"); }
    catch (e) { setHistory(prev); toast.error("No se pudo eliminar"); }
  };

  const onLogout = () => { authApi.logout(); navigate("/admin/login", { replace: true }); };

  // ================= RENDER =================
  return (
    <div className="min-h-screen bg-[#050505] text-white relative overflow-hidden">
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[1200px] h-[900px] rounded-full opacity-40 pointer-events-none"
           style={{ background: "radial-gradient(closest-side, rgba(255,138,0,0.18), transparent 70%)" }} />

      <header className="relative border-b border-white/5 bg-black/40 backdrop-blur-xl sticky top-0 z-30">
        <div className="px-5 sm:px-8 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_SRC} alt="Daos Ads" className="h-9 w-auto" />
            <div className="leading-tight border-l border-white/10 pl-3">
              <div className="font-display font-bold text-white text-[14px] tracking-tight">Studio Video</div>
              <div className="text-[10px] text-zinc-500 tracking-[0.2em] uppercase">Video Composer</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/planner" data-testid="link-planner" className="hidden sm:inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition px-3 py-1.5 rounded-lg border border-white/10 hover:border-white/20">
              Planner
            </Link>
            <Link to="/studio" data-testid="link-studio" className="hidden sm:inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition px-3 py-1.5 rounded-lg border border-white/10 hover:border-white/20">
              <ChevronLeft className="w-3.5 h-3.5" /> /studio
            </Link>
            <button onClick={onLogout} data-testid="logout" className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition px-3 py-1.5 rounded-lg border border-white/10 hover:border-white/20">
              <LogOut className="w-3.5 h-3.5" /> Salir
            </button>
          </div>
        </div>
      </header>

      <div className="relative px-5 sm:px-8 py-6">
        {!source ? (
          <SourcePicker onVideo={onPickVideo} onImage={onPickImage} />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[440px_1fr] gap-6">
            {/* LEFT: controls */}
            <aside className="space-y-4" data-testid="video-controls">
              <SourceSummaryCard sourceKind={sourceKind} sourceFile={sourceFile} videoMeta={videoMeta}
                onReset={resetSource} imageDuration={imageDuration} setImageDuration={setImageDuration} />

              <Card title="Formato">
                <div className="grid grid-cols-1 gap-2">
                  {FORMATS.map((f) => (
                    <button key={f.id} type="button" onClick={() => setFormatId(f.id)} data-testid={`fmt-${f.id}`}
                      className={`text-left text-[12px] px-3 py-2.5 rounded-lg border transition font-display ${
                        formatId === f.id ? "border-[#ff8a00] bg-[#ff8a00]/10 text-white"
                                          : "border-white/10 bg-white/[0.02] text-zinc-300 hover:border-white/25"}`}>
                      {f.label}
                    </button>
                  ))}
                </div>
                <Label className="mt-3">Ajuste al formato</Label>
                <Select value={layers.crop} onChange={(v) => setLayers((L) => ({ ...L, crop: v }))} testid="crop-select">
                  {CROP_OPTIONS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </Select>
              </Card>

              <LayerCard icon={Wand2} title="Marca (logo · handle · CTA)" enabled={layers.brand.enabled}
                onToggle={(v) => setLayers((L) => ({ ...L, brand: { ...L.brand, enabled: v } }))} testid="layer-brand">
                <Label>CTA preset</Label>
                <Select value={CTA_PRESETS.find((p) => p.value === layers.brand.cta)?.id || "custom"}
                  onChange={(id) => { const p = CTA_PRESETS.find((x) => x.id === id); if (p) setLayers((L) => ({ ...L, brand: { ...L.brand, cta: p.value } })); }}
                  testid="cta-preset">
                  {CTA_PRESETS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                  <option value="custom">Personalizado…</option>
                </Select>
                <Label className="mt-2">CTA texto</Label>
                <Input value={layers.brand.cta} onChange={(v) => setLayers((L) => ({ ...L, brand: { ...L.brand, cta: v } }))} testid="cta-input" />
                <Label className="mt-3">Color del CTA</Label>
                <div className="grid grid-cols-3 gap-2">
                  {CTA_COLOR_OPTIONS.map((opt) => (
                    <button key={opt.id} type="button" onClick={() => setLayers((L) => ({ ...L, brand: { ...L.brand, ctaColor: opt.id } }))}
                      data-testid={`cta-color-${opt.id}`}
                      className={`rounded-lg overflow-hidden border transition ${
                        layers.brand.ctaColor === opt.id ? "border-white ring-2 ring-[#ff8a00]/40" : "border-white/15 hover:border-white/30"}`}>
                      <div className="h-6 w-full" style={{ background: opt.swatch }} />
                      <div className="text-[10px] font-display py-1 text-zinc-300 text-center">{opt.label}</div>
                    </button>
                  ))}
                </div>
              </LayerCard>

              <LayerCard icon={Type} title="Texto animado" enabled={layers.text.enabled}
                onToggle={(v) => setLayers((L) => ({ ...L, text: { ...L.text, enabled: v } }))} testid="layer-text">
                <Label>Titular</Label>
                <Textarea value={layers.text.headline} onChange={(v) => setLayers((L) => ({ ...L, text: { ...L.text, headline: v } }))} rows={2} testid="text-headline" />
                <Label className="mt-2">Subtítulo</Label>
                <Textarea value={layers.text.subtitle} onChange={(v) => setLayers((L) => ({ ...L, text: { ...L.text, subtitle: v } }))} rows={2} testid="text-subtitle" />
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div><Label>Posición</Label>
                    <Select value={layers.text.position} onChange={(v) => setLayers((L) => ({ ...L, text: { ...L.text, position: v } }))} testid="text-position">
                      {TEXT_POSITIONS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                    </Select>
                  </div>
                  <div><Label>Animación</Label>
                    <Select value={layers.text.animation} onChange={(v) => setLayers((L) => ({ ...L, text: { ...L.text, animation: v } }))} testid="text-animation">
                      {ANIM_IN.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div><Label>Aparece en (s)</Label>
                    <NumberInput value={layers.text.startAt} min={0} max={totalSeconds} step={0.1}
                      onChange={(v) => setLayers((L) => ({ ...L, text: { ...L.text, startAt: v } }))} testid="text-start" />
                  </div>
                  <div><Label>Duración (s)</Label>
                    <NumberInput value={layers.text.duration} min={0.5} max={Math.max(1, totalSeconds)} step={0.1}
                      onChange={(v) => setLayers((L) => ({ ...L, text: { ...L.text, duration: v } }))} testid="text-duration" />
                  </div>
                </div>
                <Label className="mt-2">Palabras en degradado</Label>
                <Input value={layers.text.gradientWords} onChange={(v) => setLayers((L) => ({ ...L, text: { ...L.text, gradientWords: v } }))}
                  placeholder="ej: Gana más, cero" testid="text-grad" />
                <Label className="mt-2">Palabras de señal</Label>
                <Input value={layers.text.signalWords} onChange={(v) => setLayers((L) => ({ ...L, text: { ...L.text, signalWords: v } }))}
                  placeholder="ej: triplica, 30 días" testid="text-signal" />
                <Label className="mt-2">Color de señal</Label>
                <div className="flex gap-1.5">
                  {SIGNAL_COLOR_OPTIONS.map((opt) => (
                    <button key={opt.id} type="button"
                      onClick={() => setLayers((L) => ({ ...L, text: { ...L.text, signalColor: opt.id } }))}
                      data-testid={`signal-color-${opt.id}`}
                      className={`w-9 h-9 rounded-lg border grid place-items-center transition ${
                        layers.text.signalColor === opt.id ? "border-white ring-2 ring-[#ff8a00]/40" : "border-white/15 hover:border-white/30"}`}
                      style={opt.swatch ? { background: opt.swatch } : { background: "rgba(255,255,255,0.04)" }}>
                      {!opt.swatch && <X className="w-3.5 h-3.5 text-zinc-500" />}
                    </button>
                  ))}
                </div>
              </LayerCard>

              {sourceKind === "video" && (
                <LayerCard icon={Captions} title="Subtítulos" enabled={layers.subtitles.enabled}
                  onToggle={(v) => setLayers((L) => ({ ...L, subtitles: { ...L.subtitles, enabled: v } }))} testid="layer-subs">
                  <div className="flex gap-2 mb-2">
                    <button type="button" onClick={onGenerateSubtitles} disabled={transcribing}
                      className="btn-primary flex-1 justify-center text-xs disabled:opacity-60" data-testid="subs-ai-btn">
                      {transcribing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                      {transcribing ? "Transcribiendo…" : "Generar con Whisper"}
                    </button>
                    <button type="button" onClick={addManualSubtitle}
                      className="px-3 py-2 rounded-lg border border-white/10 text-zinc-300 hover:border-white/25 text-xs font-display"
                      data-testid="subs-add-btn">+ Manual</button>
                  </div>
                  <Label>Estilo</Label>
                  <Select value={layers.subtitles.style} onChange={(v) => setLayers((L) => ({ ...L, subtitles: { ...L.subtitles, style: v } }))} testid="subs-style">
                    <option value="shadow">Sombra sutil</option>
                    <option value="backdrop">Fondo semi-transparente</option>
                  </Select>
                  <div className="mt-2 max-h-52 overflow-y-auto space-y-1.5 pr-1">
                    {(layers.subtitles.items || []).map((it, i) => (
                      <div key={i} className="grid grid-cols-[auto_auto_1fr_auto] gap-1.5 items-center bg-black/30 border border-white/5 rounded p-1.5">
                        <input type="number" step="0.1" value={it.start} onChange={(e) => updateSubtitle(i, { start: Number(e.target.value) })}
                          className="w-14 bg-transparent text-[11px] text-white outline-none border border-white/10 rounded px-1.5 py-0.5" />
                        <input type="number" step="0.1" value={it.end} onChange={(e) => updateSubtitle(i, { end: Number(e.target.value) })}
                          className="w-14 bg-transparent text-[11px] text-white outline-none border border-white/10 rounded px-1.5 py-0.5" />
                        <input value={it.text} onChange={(e) => updateSubtitle(i, { text: e.target.value })}
                          className="bg-transparent text-[11px] text-white outline-none border border-white/10 rounded px-2 py-0.5" />
                        <button onClick={() => removeSubtitle(i)} className="text-zinc-500 hover:text-red-400 p-1"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    ))}
                    {(layers.subtitles.items || []).length === 0 && (
                      <div className="text-[11px] text-zinc-500 py-2 text-center">Sin subtítulos aún</div>
                    )}
                  </div>
                </LayerCard>
              )}

              <LayerCard icon={Sparkles} title="Efectos visuales" enabled={layers.effects.enabled}
                onToggle={(v) => setLayers((L) => ({ ...L, effects: { ...L.effects, enabled: v } }))} testid="layer-fx">
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Fade in</Label>
                    <Select value={String(layers.effects.fadeIn)} onChange={(v) => setLayers((L) => ({ ...L, effects: { ...L.effects, fadeIn: Number(v) } }))} testid="fx-fadein">
                      {FADE_OPTIONS.map((f) => <option key={f.id} value={f.value}>{f.label}</option>)}
                    </Select>
                  </div>
                  <div><Label>Fade out</Label>
                    <Select value={String(layers.effects.fadeOut)} onChange={(v) => setLayers((L) => ({ ...L, effects: { ...L.effects, fadeOut: Number(v) } }))} testid="fx-fadeout">
                      {FADE_OPTIONS.map((f) => <option key={f.id} value={f.value}>{f.label}</option>)}
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div><Label>Ken Burns</Label>
                    <Select value={layers.effects.kenBurns} onChange={(v) => setLayers((L) => ({ ...L, effects: { ...L.effects, kenBurns: v } }))} testid="fx-kb">
                      {KEN_BURNS_OPTIONS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
                    </Select>
                  </div>
                  <div><Label>Glitch</Label>
                    <Select value={layers.effects.glitch} onChange={(v) => setLayers((L) => ({ ...L, effects: { ...L.effects, glitch: v } }))} testid="fx-glitch">
                      {GLITCH_OPTIONS.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
                    </Select>
                  </div>
                </div>
                <Label className="mt-2">Overlay</Label>
                <Select value={layers.effects.overlay} onChange={(v) => setLayers((L) => ({ ...L, effects: { ...L.effects, overlay: v } }))} testid="fx-overlay">
                  {OVERLAY_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </Select>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-[12px] text-zinc-300 font-display">Glow Daos pulsante</span>
                  <Toggle value={layers.effects.glowPulse} onChange={(v) => setLayers((L) => ({ ...L, effects: { ...L.effects, glowPulse: v } }))} testid="fx-glow" />
                </div>
                {sourceKind === "image" && (
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[12px] text-zinc-300 font-display">Parallax (imagen)</span>
                    <Toggle value={layers.effects.parallax} onChange={(v) => setLayers((L) => ({ ...L, effects: { ...L.effects, parallax: v } }))} testid="fx-parallax" />
                  </div>
                )}
              </LayerCard>

              <LayerCard icon={Music} title="Música de fondo" enabled={layers.audio.enabled}
                onToggle={(v) => setLayers((L) => ({ ...L, audio: { ...L.audio, enabled: v } }))} testid="layer-audio">
                <label className="w-full inline-flex items-center justify-center gap-2 text-[12px] font-display text-zinc-300 hover:text-white cursor-pointer border border-dashed border-white/15 hover:border-white/30 rounded-lg px-3 py-3 relative">
                  <Upload className="w-3.5 h-3.5" /> {audioFile ? audioFile.name : "Subir audio (MP3/WAV)"}
                  <input type="file" accept="audio/*" className="absolute inset-0 opacity-0 cursor-pointer" onChange={(e) => onPickAudio(e.target.files?.[0])} data-testid="audio-input" />
                </label>
                {audioFile && (
                  <>
                    <Label className="mt-2">Volumen ({Math.round(layers.audio.volume * 100)}%)</Label>
                    <input type="range" min={0} max={1} step={0.05} value={layers.audio.volume}
                      onChange={(e) => setLayers((L) => ({ ...L, audio: { ...L.audio, volume: Number(e.target.value) } }))}
                      className="w-full accent-[#ff8a00]" data-testid="audio-volume" />
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[12px] text-zinc-300 font-display">Fade in/out</span>
                      <Toggle value={layers.audio.fade} onChange={(v) => setLayers((L) => ({ ...L, audio: { ...L.audio, fade: v } }))} testid="audio-fade" />
                    </div>
                  </>
                )}
                {sourceKind === "video" && (
                  <>
                    <Label className="mt-3">Audio original del video</Label>
                    <Select value={layers.audio.originalMode} onChange={(v) => setLayers((L) => ({ ...L, audio: { ...L.audio, originalMode: v } }))} testid="audio-orig-mode">
                      <option value="keep">Mantener</option>
                      <option value="mix">Mezclar con música</option>
                      <option value="mute">Silenciar</option>
                    </Select>
                  </>
                )}
              </LayerCard>

              {/* Actions */}
              <div className="flex gap-2 sticky bottom-4">
                <button type="button" onClick={onSaveProject} disabled={savingProject}
                  className="inline-flex items-center gap-2 px-4 py-3 rounded-xl border border-[#ff8a00]/40 bg-[#ff8a00]/10 text-white hover:bg-[#ff8a00]/20 transition text-sm font-display font-semibold disabled:opacity-60"
                  data-testid="save-project-btn">
                  {savingProject ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
                </button>
                <button type="button" onClick={onExport} disabled={exporting || !totalSeconds}
                  className="btn-primary flex-1 justify-center disabled:opacity-60" data-testid="export-btn">
                  {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  {exporting ? `Exportando ${Math.round(exportProgress * 100)}%` : "Exportar MP4"}
                </button>
              </div>
              {exporting && (
                <div className="text-[11px] text-zinc-500 truncate" data-testid="export-log">{exportLog}</div>
              )}
              {exportResult && (
                <button type="button" onClick={downloadExport} data-testid="download-mp4"
                  className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30 transition text-sm font-display font-semibold">
                  <Download className="w-4 h-4" /> Descargar {exportResult.filename}
                </button>
              )}
            </aside>

            {/* RIGHT: preview */}
            <section className="relative space-y-6" data-testid="preview-section">
              <div className="grad-border p-5 sm:p-8">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <div className="eyebrow">Preview en vivo</div>
                    <div className="font-display font-bold text-white text-lg mt-1">{format.label}</div>
                  </div>
                  <div className="text-[11px] text-zinc-500">{nowT.toFixed(1)}s / {totalSeconds.toFixed(1)}s</div>
                </div>
                <div className="flex items-center justify-center min-h-[50vh]">
                  <div style={previewStyle} className="rounded-lg overflow-hidden ring-1 ring-white/10 shadow-[0_30px_80px_-20px_rgba(255,138,0,0.35)]">
                    <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} data-testid="video-canvas" />
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-3">
                  <button type="button" onClick={() => setPlaying((p) => !p)} data-testid="play-toggle"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/15 hover:border-[#ff8a00]/60 text-sm font-display font-semibold text-white">
                    {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />} {playing ? "Pausar" : "Reproducir"}
                  </button>
                  <input type="range" min={0} max={totalSeconds || 0.1} step={0.05} value={nowT}
                    onChange={(e) => { const v = Number(e.target.value); currentTRef.current = v; setNowT(v);
                      if (source?.type === "video") source.el.currentTime = v; setPlaying(false); }}
                    className="flex-1 accent-[#ff8a00]" data-testid="scrubber" />
                </div>

                <Timeline
                  total={totalSeconds}
                  nowT={nowT}
                  layers={layers}
                  onSeek={(v) => { currentTRef.current = v; setNowT(v);
                    if (source?.type === "video") source.el.currentTime = v; setPlaying(false); }}
                />
              </div>

              {/* History */}
              <div className="grad-border p-5 sm:p-6" data-testid="video-history">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="w-7 h-7 rounded-lg bg-[#ff8a00]/15 grid place-items-center"><HistoryIcon className="w-3.5 h-3.5 text-[#ff8a00]" /></span>
                    <div className="font-display font-bold text-white text-[14px]">Proyectos guardados <span className="text-zinc-500 font-normal text-[12px] ml-1">· {history.length}</span></div>
                  </div>
                </div>
                {history.length === 0 ? (
                  <div className="text-[11px] text-zinc-500 text-center py-6 border border-dashed border-white/10 rounded-lg">
                    Aún no guardas proyectos. Cada exportación se archiva automáticamente.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {history.map((p) => (
                      <div key={p.id} className="group relative" data-testid={`history-${p.id}`}>
                        <button type="button" onClick={() => onLoadProject(p.id)} data-testid={`history-load-${p.id}`}
                          className="block w-full text-left rounded-lg overflow-hidden ring-1 ring-white/10 hover:ring-[#ff8a00]/60 transition">
                          <div className="w-full bg-[#050505]" style={{ aspectRatio: `${FORMATS.find((f) => f.id === p.format_id)?.width || 1} / ${FORMATS.find((f) => f.id === p.format_id)?.height || 1}` }}>
                            <img src={`data:${p.thumbnail_mime || "image/jpeg"};base64,${p.thumbnail_base64}`} alt={p.name} loading="lazy" className="w-full h-full object-cover" />
                          </div>
                          <div className="p-2 bg-black/40">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-[#ff8a00] font-display font-semibold truncate">{FORMATS.find((f) => f.id === p.format_id)?.label?.split(" · ")[0]}</div>
                            <div className="text-[11px] text-white truncate mt-0.5">{p.name}</div>
                          </div>
                        </button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); onDeleteProject(p.id); }} data-testid={`history-delete-${p.id}`}
                          className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/70 border border-white/10 text-zinc-300 hover:bg-red-500/80 hover:border-red-400 grid place-items-center opacity-0 group-hover:opacity-100 transition">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          </div>
        )}
      </div>

      <Toaster theme="dark" position="top-right"
        toastOptions={{ style: { background: "#0a0a0a", border: "1px solid rgba(255,138,0,0.25)", color: "#fff" } }} />
    </div>
  );
}

// ============================================================
// UI primitives
// ============================================================
function SourcePicker({ onVideo, onImage }) {
  return (
    <div className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="source-picker">
      <label className="cursor-pointer relative grad-border p-8 flex flex-col items-center gap-4 hover:bg-white/[0.02] transition" data-testid="pick-video">
        <div className="w-16 h-16 rounded-2xl bg-[#ff8a00]/15 grid place-items-center"><Video className="w-7 h-7 text-[#ff8a00]" /></div>
        <div className="text-center">
          <div className="font-display font-bold text-white text-xl">Subir video</div>
          <div className="text-[12px] text-zinc-400 mt-1.5">MP4 · MOV · WebM · máx 200MB</div>
        </div>
        <input type="file" accept="video/*" className="absolute inset-0 opacity-0 cursor-pointer"
          onChange={(e) => onVideo(e.target.files?.[0])} data-testid="video-input" />
      </label>
      <label className="cursor-pointer relative grad-border p-8 flex flex-col items-center gap-4 hover:bg-white/[0.02] transition" data-testid="pick-image">
        <div className="w-16 h-16 rounded-2xl bg-[#ff3b1f]/15 grid place-items-center"><ImageIcon className="w-7 h-7 text-[#ff3b1f]" /></div>
        <div className="text-center">
          <div className="font-display font-bold text-white text-xl">Subir imagen</div>
          <div className="text-[12px] text-zinc-400 mt-1.5">JPG · PNG · WebP · máx 20MB</div>
          <div className="text-[10px] text-zinc-500 mt-1">Se convierte en video animado (5-60s)</div>
        </div>
        <input type="file" accept="image/*" className="absolute inset-0 opacity-0 cursor-pointer"
          onChange={(e) => onImage(e.target.files?.[0])} data-testid="image-input" />
      </label>
    </div>
  );
}

function SourceSummaryCard({ sourceKind, sourceFile, videoMeta, onReset, imageDuration, setImageDuration }) {
  return (
    <Card title="Origen" testid="source-summary">
      <div className="flex items-center gap-3">
        <span className="w-9 h-9 rounded-lg bg-[#ff8a00]/15 grid place-items-center">
          {sourceKind === "video" ? <Video className="w-4 h-4 text-[#ff8a00]" /> : <ImageIcon className="w-4 h-4 text-[#ff8a00]" />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] text-white font-display font-semibold truncate">{sourceFile?.name}</div>
          <div className="text-[11px] text-zinc-500">
            {sourceKind === "video" ? `${videoMeta?.width}×${videoMeta?.height} · ${videoMeta?.duration?.toFixed(1)}s` : "Imagen"}
          </div>
        </div>
        <button onClick={onReset} className="p-2 text-zinc-400 hover:text-white transition" data-testid="reset-source"><X className="w-4 h-4" /></button>
      </div>
      {sourceKind === "image" && (
        <>
          <Label className="mt-3">Duración del video</Label>
          <div className="grid grid-cols-5 gap-1.5">
            {IMAGE_DURATIONS.map((s) => (
              <button key={s} type="button" onClick={() => setImageDuration(s)} data-testid={`img-dur-${s}`}
                className={`text-[11px] py-2 rounded border transition font-display ${
                  imageDuration === s ? "border-[#ff8a00] bg-[#ff8a00]/10 text-white"
                                      : "border-white/10 text-zinc-300 hover:border-white/25"}`}>
                {s}s
              </button>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}

function Card({ title, testid, children }) {
  return (
    <div className="grad-border p-4" data-testid={testid}>
      <div className="text-[13px] font-display font-bold text-white tracking-tight mb-3">{title}</div>
      {children}
    </div>
  );
}

function LayerCard({ icon: Icon, title, enabled, onToggle, testid, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="grad-border p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <span className="w-7 h-7 rounded-lg bg-[#ff8a00]/15 grid place-items-center">
          <Icon className="w-3.5 h-3.5 text-[#ff8a00]" />
        </span>
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex-1 text-left font-display font-bold text-white text-[13px] tracking-tight">
          {title}
        </button>
        <Toggle value={enabled} onChange={onToggle} testid={`${testid}-toggle`} />
      </div>
      {open && enabled && <div>{children}</div>}
    </div>
  );
}

function Label({ children, className = "" }) {
  return <label className={`block text-[10px] tracking-[0.2em] uppercase text-zinc-400 font-display font-semibold mb-1 ${className}`}>{children}</label>;
}

function Input({ value, onChange, testid, ...rest }) {
  return <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder:text-zinc-600 focus:border-[#ff8a00] focus:ring-2 focus:ring-[#ff8a00]/20 transition outline-none" {...rest} />;
}
function Textarea({ value, onChange, testid, rows = 2, ...rest }) {
  return <textarea value={value} rows={rows} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder:text-zinc-600 focus:border-[#ff8a00] focus:ring-2 focus:ring-[#ff8a00]/20 transition outline-none resize-none" {...rest} />;
}
function Select({ value, onChange, children, testid }) {
  return <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-[#ff8a00] focus:ring-2 focus:ring-[#ff8a00]/20 transition outline-none">
    {children}
  </select>;
}
function NumberInput({ value, onChange, testid, min, max, step }) {
  return <input type="number" value={value} min={min} max={max} step={step}
    onChange={(e) => onChange(Number(e.target.value))} data-testid={testid}
    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-[#ff8a00] focus:ring-2 focus:ring-[#ff8a00]/20 transition outline-none" />;
}
function Toggle({ value, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!value)} data-testid={testid}
      className={`w-10 h-5 rounded-full border transition relative ${value ? "bg-[#ff8a00] border-[#ff8a00]" : "bg-black/40 border-white/15"}`}>
      <span className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white transition ${value ? "left-5" : "left-0.5"}`} />
    </button>
  );
}

// ============================================================
// Timeline — visual layout of layer activity across the total duration
// ============================================================
function Timeline({ total, nowT, layers, onSeek }) {
  const trackRef = useRef(null);
  if (!total || total <= 0) return null;

  const pct = (t) => `${Math.max(0, Math.min(100, (t / total) * 100))}%`;
  const seg = (start, end, color, testid, title) => {
    const s = Math.max(0, Math.min(total, start));
    const e = Math.max(s, Math.min(total, end));
    if (e <= s) return null;
    return (
      <div key={testid + s} data-testid={testid} title={title}
        className="absolute top-0 bottom-0 rounded"
        style={{ left: pct(s), width: pct(e - s), background: color, opacity: 0.85 }} />
    );
  };

  const onTrackClick = (e) => {
    const rect = trackRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const t = Math.max(0, Math.min(total, (x / rect.width) * total));
    onSeek(t);
  };

  const rows = [];

  // Row 1: Fade in / out (grays)
  const fadeSegs = [];
  const fi = layers.effects?.fadeIn || 0;
  const fo = layers.effects?.fadeOut || 0;
  if (layers.effects?.enabled && fi > 0) fadeSegs.push(seg(0, fi, "rgba(148,163,184,0.55)", "tl-fadein", `Fade in ${fi}s`));
  if (layers.effects?.enabled && fo > 0) fadeSegs.push(seg(total - fo, total, "rgba(148,163,184,0.55)", "tl-fadeout", `Fade out ${fo}s`));
  rows.push({ label: "Fades", color: "#94A3B8", segments: fadeSegs, testid: "tl-row-fades" });

  // Row 2: Text animated (orange)
  const textSegs = [];
  if (layers.text?.enabled) {
    const start = Math.max(0, Number(layers.text.startAt) || 0);
    const dur = Math.max(0.1, Number(layers.text.duration) || 0);
    textSegs.push(seg(start, start + dur, "#FF8A00", "tl-text", `Texto ${start}s → ${(start + dur).toFixed(1)}s`));
  }
  rows.push({ label: "Texto", color: "#FF8A00", segments: textSegs, testid: "tl-row-text" });

  // Row 3: Subtitles (gold)
  const subSegs = [];
  if (layers.subtitles?.enabled) {
    (layers.subtitles.items || []).forEach((it, i) => {
      subSegs.push(seg(it.start, it.end, "#FFD23F", `tl-sub-${i}`, it.text));
    });
  }
  rows.push({ label: "Subtítulos", color: "#FFD23F", segments: subSegs, testid: "tl-row-subs" });

  // Row 4: Music (red)
  const audioSegs = [];
  if (layers.audio?.enabled) {
    audioSegs.push(seg(0, total, "rgba(244,63,94,0.55)", "tl-audio", "Música"));
  }
  rows.push({ label: "Música", color: "#F43F5E", segments: audioSegs, testid: "tl-row-audio" });

  return (
    <div className="mt-4 rounded-lg border border-white/5 bg-black/30 p-3" data-testid="tl-container">
      <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-display font-semibold mb-2">
        Línea de tiempo
      </div>
      <div ref={trackRef} onClick={onTrackClick} className="relative cursor-pointer select-none" data-testid="tl-track">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center gap-2 mb-1.5 last:mb-0" data-testid={r.testid}>
            <div className="w-16 shrink-0 text-[10px] text-zinc-500 font-display">{r.label}</div>
            <div className="relative flex-1 h-3 rounded bg-white/[0.04] overflow-hidden">
              {r.segments}
            </div>
          </div>
        ))}
        {/* Playhead */}
        <div className="absolute top-0 bottom-0 pointer-events-none" style={{ left: `calc(64px + (100% - 64px) * ${(nowT / total)})` }}>
          <div className="w-px h-full bg-white/80" />
          <div className="absolute -top-1 -translate-x-1/2 w-2 h-2 rounded-full bg-white" />
        </div>
      </div>
      <div className="flex justify-between text-[10px] text-zinc-600 mt-1.5 font-display">
        <span>0.0s</span>
        <span>{(total / 2).toFixed(1)}s</span>
        <span>{total.toFixed(1)}s</span>
      </div>
    </div>
  );
}
